import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DownloadIcon from "@mui/icons-material/Download";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useMemo, useRef, useState } from "react";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { fetchActivitiesByAssessment, fetchModuleBuilderCatalog } from "./catalogAPI";
import { flattenCatalog, inferPreviewKind } from "./catalogModel";
import type {
  CatalogQuestion,
  FlatLibraryQuestion,
  ModuleBuilderCatalogResponse
} from "./catalogTypes";
import { ActivitiesEditor } from "./ActivitiesEditor";
import { compactFieldSx } from "./formStyles";
import { fetchInstrumentDetail } from "./catalogAPI";
type GetInstrumentDetail = Awaited<
  ReturnType<typeof fetchInstrumentDetail>
>["instrument"];
import {
  InstrumentTitleAutocomplete,
  type InstrumentSuggestion
} from "./InstrumentTitleAutocomplete";
import {
  AssessmentTitleAutocomplete,
  type AssessmentSuggestion
} from "./AssessmentTitleAutocomplete";
import { NetworkSelector } from "./NetworkSelector";
import { ReusableActivitiesPicker } from "./ReusableActivitiesPicker";
import { OutlineRail } from "./OutlineRail";
import { PreviewRail } from "./PreviewRail";
import { QuestionsEditor } from "./QuestionsEditor";
import { buildCsv, downloadCsv, suggestedFilename } from "./csvExport";
import {
  defaultDraft,
  defaultInstrument,
  loadDraft,
  saveDraft
} from "./draft";
import { computeReadiness, parseActivities } from "./model";
import {
  DEFAULT_ASSESSMENT_SETTINGS,
  type ModuleBuilderDraft,
  type InstrumentDraft,
  type QuestionOverride
} from "./types";

const pageDetails = {
  className: "ModuleBuilderPage",
  headerText: "Module builder",
  headerVariant: "h2" as const
};

const RAIL_LEFT = 216;
const RAIL_RIGHT = 320;

function useDebouncedSave(draft: ModuleBuilderDraft): boolean {
  const [savedAt, setSavedAt] = useState<number>(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => {
    const t = window.setTimeout(() => {
      saveDraft(draftRef.current);
      setSavedAt(Date.now());
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return savedAt > 0;
}

export function ModuleBuilderPage() {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up("lg"));

  const [draft, setDraft] = useState<ModuleBuilderDraft>(() => loadDraft());
  const [catalog, setCatalog] = useState<ModuleBuilderCatalogResponse | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [outlineOpen, setOutlineOpen] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(true);
  // Per-source-instrument detail cache (questions + answer options), filled
  // on demand whenever a draft instrument has sourceInstrumentId set.
  const [instrumentDetailById, setInstrumentDetailById] = useState<
    Record<string, GetInstrumentDetail>
  >({});
  // Detail-fetch lifecycle for sourced instruments. Read by computeReadiness
  // so we don't allow Export while a fetch is in flight or has failed —
  // exportCsv reads from instrumentDetailById and would otherwise emit an
  // instrument block with no questions.
  const [loadingInstrumentDetailIds, setLoadingInstrumentDetailIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [failedInstrumentDetailIds, setFailedInstrumentDetailIds] = useState<
    ReadonlySet<string>
  >(new Set());
  // Per-source-assessment activity list. Hoisted here so both the
  // ReusableActivitiesPicker and ActivitiesEditor can use the same data
  // without double-fetching. Used to highlight which draft activities will
  // reuse an existing DB row at import time.
  const [sourceActivitiesByAssessmentId, setSourceActivitiesByAssessmentId] = useState<
    Record<string, Array<{ id: string; title: string; description: string | null }>>
  >({});
  // Failed source-assessment fetches. Without this set the picker stays on
  // an indefinite spinner when the fetch errors. Cleared when the user picks
  // an assessment via changeAssessmentTitleAndSource so re-picking retries.
  const [failedSourceAssessmentIds, setFailedSourceAssessmentIds] = useState<
    ReadonlySet<string>
  >(new Set());
  // Single source of truth for "is this component still mounted". Used by the
  // sourced-instrument and source-assessment fetch effects to skip state
  // updates after unmount WITHOUT cancelling in-flight fetches when deps
  // change — a closure-local `cancelled` token would be flipped to true by
  // the cleanup of the previous effect run, silently dropping the very fetch
  // that effect started.
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    // Reset on (re)mount. StrictMode in dev runs mount → cleanup → mount, so
    // without this reset the ref would stay `true` after the first cleanup
    // and every subsequent fetch resolution would silently bail.
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);
  const hasSaved = useDebouncedSave(draft);

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    fetchModuleBuilderCatalog({ instrumentLimit: 300 })
      .then((res) => {
        if (!active) return;
        setCatalog(res);
        setCatalogError(null);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setCatalogError(err instanceof Error ? err.message : "Failed to load catalog.");
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Fetch full instrument detail for any sourced instruments not in the
  // cache yet. Idempotent: existing cache entries are kept. Readiness gates
  // Export on positive presence in instrumentDetailById (via the `loaded`
  // set below), so the only thing this effect needs to do is start the
  // fetch and record success/failure in the cache + failed-set. Failed
  // entries are sticky; changeInstrumentTitleAndSource clears them so
  // re-picking the same source retries.
  //
  // Internal dedup state (loadingInstrumentDetailIds, failedInstrumentDetailIds)
  // is intentionally NOT in the dep array: including it would cause the
  // effect to re-run synchronously after we add a sid to the loading set,
  // and the cleanup of the previous run would flip a `cancelled` token to
  // true — silently dropping the very fetch we just started. The setter
  // updaters already dedupe correctly on race, and `isUnmountedRef` handles
  // the only legitimate cancellation case (unmount).
  useEffect(() => {
    for (const instr of draft.instruments) {
      const sid = instr.sourceInstrumentId;
      if (!sid) continue;
      if (instrumentDetailById[sid]) continue;
      if (loadingInstrumentDetailIds.has(sid)) continue;
      if (failedInstrumentDetailIds.has(sid)) continue;
      setLoadingInstrumentDetailIds((prev) => {
        if (prev.has(sid)) return prev;
        const next = new Set(prev);
        next.add(sid);
        return next;
      });
      fetchInstrumentDetail(sid)
        .then((res) => {
          if (isUnmountedRef.current) return;
          setInstrumentDetailById((prev) =>
            prev[sid] ? prev : { ...prev, [sid]: res.instrument }
          );
          setLoadingInstrumentDetailIds((prev) => {
            if (!prev.has(sid)) return prev;
            const next = new Set(prev);
            next.delete(sid);
            return next;
          });
        })
        .catch(() => {
          if (isUnmountedRef.current) return;
          setLoadingInstrumentDetailIds((prev) => {
            if (!prev.has(sid)) return prev;
            const next = new Set(prev);
            next.delete(sid);
            return next;
          });
          setFailedInstrumentDetailIds((prev) => {
            if (prev.has(sid)) return prev;
            const next = new Set(prev);
            next.add(sid);
            return next;
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.instruments, instrumentDetailById]);

  // Same pattern as the instrument-detail fetch: cache on success, record on
  // failure, don't auto-retry. Failed entries are sticky so we don't spin in
  // a tight retry loop on a persistently failing assessment;
  // changeAssessmentTitleAndSource clears the failed entry on re-pick. See
  // the comment on the instrument-detail effect above for why
  // failedSourceAssessmentIds is not in the dep array.
  useEffect(() => {
    for (const instr of draft.instruments) {
      const sid = instr.sourceAssessmentId;
      if (!sid) continue;
      if (sourceActivitiesByAssessmentId[sid]) continue;
      if (failedSourceAssessmentIds.has(sid)) continue;
      fetchActivitiesByAssessment(sid)
        .then((res) => {
          if (isUnmountedRef.current) return;
          const list = (res.activities ?? []).map((a) => ({
            id: a.id,
            title: a.title,
            description: a.description ?? null
          }));
          setSourceActivitiesByAssessmentId((prev) =>
            prev[sid] ? prev : { ...prev, [sid]: list }
          );
        })
        .catch(() => {
          if (isUnmountedRef.current) return;
          setFailedSourceAssessmentIds((prev) => {
            if (prev.has(sid)) return prev;
            const next = new Set(prev);
            next.add(sid);
            return next;
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.instruments, sourceActivitiesByAssessmentId]);

  const flatLibrary = useMemo<FlatLibraryQuestion[]>(
    () => (catalog ? flattenCatalog(catalog.instruments) : []),
    [catalog]
  );
  const flatByQuestionId = useMemo(() => {
    const map = new Map<string, FlatLibraryQuestion>();
    for (const q of flatLibrary) map.set(q.questionId, q);
    return map;
  }, [flatLibrary]);
  const catalogQuestionsById = useMemo(() => {
    const map = new Map<string, CatalogQuestion>();
    if (catalog) {
      for (const inst of catalog.instruments) {
        for (const q of inst.questions) map.set(q.id, q);
      }
    }
    return map;
  }, [catalog]);

  // For each draft instrument that's sourced, convert the loaded instrument
  // detail's questions into FlatLibraryQuestion shapes — same lookup contract
  // used everywhere else, so the preview can render them without a special
  // code path. Empty when the source detail hasn't loaded yet.
  const inheritedFlatsByInstrumentId = useMemo<Record<string, FlatLibraryQuestion[]>>(() => {
    const out: Record<string, FlatLibraryQuestion[]> = {};
    for (const instr of draft.instruments) {
      const sid = instr.sourceInstrumentId;
      if (!sid) continue;
      const detail = instrumentDetailById[sid];
      if (!detail) continue;
      out[instr.id] = (detail.questions ?? []).map((q) => {
        const answers = (q.answerOptions ?? []).map((a) => ({
          label: (a.anchor && a.anchor.trim()) || a.value,
          value: a.value,
          prompt: a.helpText ?? null
        }));
        return {
          questionId: q.id,
          title: q.title,
          prompt: q.defaultPrompt ?? "",
          inputTypeName: q.inputType ?? null,
          kind: inferPreviewKind(q.inputType ?? null, answers.length),
          usageActivityCount: 0,
          requiredDefault: q.required ?? null,
          answers,
          answerSetVariants: [],
          searchBlob: ""
        };
      });
    }
    return out;
  }, [draft.instruments, instrumentDetailById]);

  const parsedByInstrumentId = useMemo<Record<string, ReturnType<typeof parseActivities>>>(() => {
    const out: Record<string, ReturnType<typeof parseActivities>> = {};
    for (const instr of draft.instruments) out[instr.id] = parseActivities(instr);
    return out;
  }, [draft.instruments]);

  const activityCountByInstrumentId = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const id of Object.keys(parsedByInstrumentId)) {
      out[id] = parsedByInstrumentId[id]!.length;
    }
    return out;
  }, [parsedByInstrumentId]);

  // Build a positive "loaded" set from instrumentDetailById's keys. Passing
  // this (rather than a "loading" set) closes the render-vs-effect race:
  // anything not in `loaded` and not in `failed` is treated as still loading
  // by readiness, including the tick between sourceInstrumentId being set
  // and the fetch effect firing.
  const loadedInstrumentDetailIds = useMemo(
    () => new Set(Object.keys(instrumentDetailById)),
    [instrumentDetailById]
  );

  const readiness = useMemo(
    () =>
      computeReadiness(
        draft,
        parsedByInstrumentId,
        {
          loaded: loadedInstrumentDetailIds,
          failed: failedInstrumentDetailIds
        },
        {
          loaded: !!catalog,
          error: catalogError
        }
      ),
    [
      draft,
      parsedByInstrumentId,
      loadedInstrumentDetailIds,
      failedInstrumentDetailIds,
      catalog,
      catalogError
    ]
  );

  const activeInstrument =
    draft.instruments.find((i) => i.id === draft.activeInstrumentId) ?? draft.instruments[0]!;

  function patchInstrument(id: string, patch: Partial<InstrumentDraft>) {
    setDraft((prev) => ({
      ...prev,
      instruments: prev.instruments.map((i) => (i.id === id ? { ...i, ...patch } : i))
    }));
  }

  function patchActiveInstrument(patch: Partial<InstrumentDraft>) {
    patchInstrument(activeInstrument.id, patch);
  }

  function addInstrument() {
    setDraft((prev) => {
      const created = defaultInstrument(prev.instruments.length);
      return {
        ...prev,
        instruments: [...prev.instruments, created],
        activeInstrumentId: created.id,
        instrumentQuestionIdsById: { ...prev.instrumentQuestionIdsById, [created.id]: [] },
        instrumentQuestionRequiredById: {
          ...prev.instrumentQuestionRequiredById,
          [created.id]: {}
        },
        instrumentQuestionAnswerSetById: {
          ...prev.instrumentQuestionAnswerSetById,
          [created.id]: {}
        },
        instrumentQuestionOverridesById: {
          ...prev.instrumentQuestionOverridesById,
          [created.id]: {}
        }
      };
    });
  }

  function removeInstrument(id: string) {
    setDraft((prev) => {
      if (prev.instruments.length <= 1) return prev;
      const remaining = prev.instruments.filter((i) => i.id !== id);
      const nextActive =
        prev.activeInstrumentId === id ? remaining[0]!.id : prev.activeInstrumentId;
      const drop = (rec: Record<string, unknown>) => {
        const out = { ...rec };
        delete out[id];
        return out;
      };
      return {
        ...prev,
        instruments: remaining,
        activeInstrumentId: nextActive,
        instrumentQuestionIdsById: drop(prev.instrumentQuestionIdsById) as Record<string, string[]>,
        instrumentQuestionRequiredById: drop(prev.instrumentQuestionRequiredById) as Record<
          string,
          Record<string, boolean>
        >,
        instrumentQuestionAnswerSetById: drop(prev.instrumentQuestionAnswerSetById) as Record<
          string,
          Record<string, string>
        >,
        instrumentQuestionOverridesById: drop(prev.instrumentQuestionOverridesById) as Record<
          string,
          Record<string, QuestionOverride>
        >
      };
    });
  }

  function selectInstrument(id: string) {
    setDraft((prev) => ({ ...prev, activeInstrumentId: id }));
  }

  function setModuleTitle(title: string) {
    setDraft((prev) => ({ ...prev, moduleTitle: title }));
  }

  function setTargetNetwork(network: { id: string; sponsorId: string | null } | null) {
    setDraft((prev) => ({
      ...prev,
      targetNetworkId: network?.id ?? undefined,
      targetSponsorId: network?.sponsorId ?? undefined
    }));
  }

  function changeInstrumentTitle(id: string, newTitle: string) {
    // Legacy path (no source detection). Kept for typing in fields that
    // don't go through the autocomplete (none right now, but cheap to keep).
    setDraft((prev) => {
      const instr = prev.instruments.find((i) => i.id === id);
      if (!instr) return prev;
      const oldTitle = instr.title.trim();
      const oldDesc = instr.description.trim();
      const shouldMirror = !oldDesc || oldDesc === oldTitle;
      return {
        ...prev,
        instruments: prev.instruments.map((i) =>
          i.id === id
            ? { ...i, title: newTitle, description: shouldMirror ? newTitle : i.description }
            : i
        )
      };
    });
  }

  // Called by the InstrumentTitleAutocomplete. `match` is non-null whenever
  // the typed/selected title exactly matches an existing instrument — that
  // signals reuse, so we lock description + sourceInstrumentId.
  function changeInstrumentTitleAndSource(
    id: string,
    next: { title: string; match: InstrumentSuggestion | null }
  ) {
    if (next.match) {
      // Clear any prior failure for this sid so the fetch effect retries.
      const sid = next.match.id;
      setFailedInstrumentDetailIds((prev) => {
        if (!prev.has(sid)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(sid);
        return nextSet;
      });
    }
    setDraft((prev) => {
      const instr = prev.instruments.find((i) => i.id === id);
      if (!instr) return prev;
      if (next.match) {
        // Reuse mode: snap description to the source's, set sourceInstrumentId.
        return {
          ...prev,
          instruments: prev.instruments.map((i) =>
            i.id === id
              ? {
                  ...i,
                  title: next.match!.title,
                  description: next.match!.description ?? "",
                  sourceInstrumentId: next.match!.id
                }
              : i
          )
        };
      }
      // Free-text mode: clear sourceInstrumentId; description auto-mirrors as
      // before if the user hadn't customized it.
      const oldTitle = instr.title.trim();
      const oldDesc = instr.description.trim();
      const wasMirror = !oldDesc || oldDesc === oldTitle;
      const newDesc = wasMirror ? next.title : instr.description;
      return {
        ...prev,
        instruments: prev.instruments.map((i) =>
          i.id === id
            ? {
                ...i,
                title: next.title,
                description: newDesc,
                sourceInstrumentId: undefined,
                // Clearing the instrument source also clears the assessment
                // source (the assessment was scoped to the previous instrument).
                sourceAssessmentId: undefined
              }
            : i
        )
      };
    });
  }

  function changeAssessmentTitleAndSource(
    id: string,
    next: { title: string; match: AssessmentSuggestion | null }
  ) {
    if (next.match) {
      // Clear any prior failure for this sid so the fetch effect retries.
      const sid = next.match.id;
      setFailedSourceAssessmentIds((prev) => {
        if (!prev.has(sid)) return prev;
        const nextSet = new Set(prev);
        nextSet.delete(sid);
        return nextSet;
      });
    }
    setDraft((prev) => {
      const instr = prev.instruments.find((i) => i.id === id);
      if (!instr) return prev;
      if (next.match) {
        // Reuse mode: lock description + settings to the source's values.
        return {
          ...prev,
          instruments: prev.instruments.map((i) =>
            i.id === id
              ? {
                  ...i,
                  sourceAssessmentId: next.match!.id,
                  assessmentSettings: {
                    ...i.assessmentSettings,
                    title: next.match!.title,
                    description: next.match!.description ?? "",
                    requireSelfAssessment:
                      next.match!.requireTraineeSelfAssessment ??
                      i.assessmentSettings.requireSelfAssessment,
                    creationDurationHours:
                      next.match!.creationDurationHours ??
                      i.assessmentSettings.creationDurationHours,
                    expirationDurationHours:
                      next.match!.expirationDurationHours ??
                      i.assessmentSettings.expirationDurationHours
                  }
                }
              : i
          )
        };
      }
      // Free-text: clear sourceAssessmentId; description auto-mirrors as before.
      const oldTitle = instr.assessmentSettings.title.trim();
      const oldDesc = instr.assessmentSettings.description.trim();
      const wasMirror = !oldDesc || oldDesc === oldTitle;
      const newDesc = wasMirror ? next.title : instr.assessmentSettings.description;
      return {
        ...prev,
        instruments: prev.instruments.map((i) =>
          i.id === id
            ? {
                ...i,
                sourceAssessmentId: undefined,
                assessmentSettings: {
                  ...i.assessmentSettings,
                  title: next.title,
                  description: newDesc
                }
              }
            : i
        )
      };
    });
  }

  function resetDraft() {
    if (window.confirm("Reset the draft? All unsaved changes will be lost.")) {
      setDraft(defaultDraft());
    }
  }

  function exportCsv() {
    // For sourced instruments, the CSV path needs the raw inherited questions
    // (with helpText, selfAssessmentPrompt, and full answer-option list)
    // straight from the loaded detail — the page's "FlatLibraryQuestion"
    // adapter strips those for the catalog-question rendering path.
    const inheritedQuestionsByInstrumentId: Record<
      string,
      Parameters<typeof buildCsv>[3] extends Record<string, infer U> | undefined ? U : never
    > = {};
    for (const instr of draft.instruments) {
      const sid = instr.sourceInstrumentId;
      if (!sid) continue;
      const detail = instrumentDetailById[sid];
      if (!detail) continue;
      inheritedQuestionsByInstrumentId[instr.id] = (detail.questions ?? []).map((q) => ({
        id: q.id,
        title: q.title,
        defaultPrompt: q.defaultPrompt ?? null,
        selfAssessmentPrompt: q.selfAssessmentPrompt ?? null,
        helpText: q.helpText ?? null,
        required: q.required ?? null,
        inputType: q.inputType ?? null,
        answerOptions: (q.answerOptions ?? []).map((a) => ({
          id: a.id,
          anchor: a.anchor ?? null,
          value: a.value,
          sortingIndex: a.sortingIndex ?? null,
          helpText: a.helpText ?? null
        }))
      }));
    }
    const csv = buildCsv(
      draft,
      parsedByInstrumentId,
      flatByQuestionId,
      inheritedQuestionsByInstrumentId
    );
    downloadCsv(suggestedFilename(draft.moduleTitle), csv);
  }

  return (
    <PageWrapper {...pageDetails}>
      <Header
        moduleTitle={draft.moduleTitle}
        onTitleChange={setModuleTitle}
        targetNetworkId={draft.targetNetworkId}
        onSelectNetwork={setTargetNetwork}
        savedRecently={hasSaved}
        canExport={readiness.blocking === 0}
        catalogLoading={catalogLoading}
        catalogError={catalogError}
        onReset={resetDraft}
        onExport={exportCsv}
      />
      {draft.targetNetworkId ? (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate(isWide, outlineOpen, previewOpen),
          gridTemplateAreas: gridAreas(isWide),
          gap: 2,
          alignItems: "start",
          mt: 2
        }}
      >
        <Box sx={{ gridArea: "outline" }}>
          {outlineOpen ? (
            <OutlineRail
              draft={draft}
              activityCountByInstrumentId={activityCountByInstrumentId}
              onSelectInstrument={selectInstrument}
              onAddInstrument={addInstrument}
              onRemoveInstrument={removeInstrument}
              onModuleTitleChange={setModuleTitle}
              onCollapse={() => setOutlineOpen(false)}
            />
          ) : (
            <CollapsedRail
              label="Outline"
              onExpand={() => setOutlineOpen(true)}
              side="left"
            />
          )}
        </Box>

        <Box sx={{ gridArea: "workspace", minWidth: 0 }}>
          <Workspace
            instrument={activeInstrument}
            targetSponsorId={draft.targetSponsorId}
            sourceInstrumentDetail={
              activeInstrument.sourceInstrumentId
                ? instrumentDetailById[activeInstrument.sourceInstrumentId] ?? null
                : null
            }
            sourceActivities={
              activeInstrument.sourceAssessmentId
                ? sourceActivitiesByAssessmentId[activeInstrument.sourceAssessmentId] ?? null
                : null
            }
            sourceActivitiesFailed={
              !!activeInstrument.sourceAssessmentId &&
              failedSourceAssessmentIds.has(activeInstrument.sourceAssessmentId)
            }
            flatLibrary={flatLibrary}
            flatByQuestionId={flatByQuestionId}
            catalogQuestionsById={catalogQuestionsById}
            selectedIds={draft.instrumentQuestionIdsById[activeInstrument.id] ?? []}
            requiredById={draft.instrumentQuestionRequiredById[activeInstrument.id] ?? {}}
            answerSetById={draft.instrumentQuestionAnswerSetById[activeInstrument.id] ?? {}}
            overridesById={draft.instrumentQuestionOverridesById[activeInstrument.id] ?? {}}
            onPatchInstrument={patchActiveInstrument}
            onChangeInstrumentTitleAndSource={(next) =>
              changeInstrumentTitleAndSource(activeInstrument.id, next)
            }
            onChangeAssessmentTitleAndSource={(next) =>
              changeAssessmentTitleAndSource(activeInstrument.id, next)
            }
            onQuestionsChange={(next) => {
              setDraft((prev) => ({
                ...prev,
                instrumentQuestionIdsById: {
                  ...prev.instrumentQuestionIdsById,
                  [activeInstrument.id]: next.selectedIds
                },
                instrumentQuestionRequiredById: {
                  ...prev.instrumentQuestionRequiredById,
                  [activeInstrument.id]: next.requiredById
                },
                instrumentQuestionAnswerSetById: {
                  ...prev.instrumentQuestionAnswerSetById,
                  [activeInstrument.id]: next.answerSetById
                },
                instrumentQuestionOverridesById: {
                  ...prev.instrumentQuestionOverridesById,
                  [activeInstrument.id]: next.overridesById
                }
              }));
            }}
          />
        </Box>

        <Box sx={{ gridArea: "preview", minWidth: 0 }}>
          {previewOpen ? (
            <PreviewRail
              draft={draft}
              parsedByInstrumentId={parsedByInstrumentId}
              sourceActivitiesByAssessmentId={sourceActivitiesByAssessmentId}
              inheritedFlatsByInstrumentId={inheritedFlatsByInstrumentId}
              readiness={readiness}
              flatByQuestionId={flatByQuestionId}
              onCollapse={() => setPreviewOpen(false)}
            />
          ) : (
            <CollapsedRail
              label="Preview"
              onExpand={() => setPreviewOpen(true)}
              side="right"
            />
          )}
        </Box>
      </Box>
      ) : null}
    </PageWrapper>
  );
}

function gridTemplate(isWide: boolean, outlineOpen: boolean, previewOpen: boolean) {
  // minmax(min, %) keeps the rail at `min` on smaller screens but lets it grow
  // up to a percentage of the container on wider screens, so the workspace
  // doesn't balloon and the rails don't look squished by comparison.
  const left = outlineOpen ? `minmax(${RAIL_LEFT}px, 18%)` : "44px";
  if (!isWide) {
    // Narrow: outline + workspace on row 1, preview spans both columns on row 2.
    return `${left} minmax(0, 1fr)`;
  }
  const right = previewOpen ? `minmax(${RAIL_RIGHT}px, 22%)` : "44px";
  return `${left} minmax(0, 1fr) ${right}`;
}

function gridAreas(isWide: boolean): string {
  if (isWide) {
    return `"outline workspace preview"`;
  }
  return `"outline workspace" "preview preview"`;
}

function Header({
  moduleTitle,
  onTitleChange,
  targetNetworkId,
  onSelectNetwork,
  savedRecently,
  canExport,
  catalogLoading,
  catalogError,
  onReset,
  onExport
}: {
  moduleTitle: string;
  onTitleChange: (v: string) => void;
  targetNetworkId?: string;
  onSelectNetwork: (network: { id: string; sponsorId: string | null } | null) => void;
  savedRecently: boolean;
  canExport: boolean;
  catalogLoading: boolean;
  catalogError: string | null;
  onReset: () => void;
  onExport: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
      >
        <TextField
          variant="standard"
          fullWidth
          value={moduleTitle}
          placeholder="Untitled module"
          onChange={(e) => onTitleChange(e.target.value)}
          inputProps={{
            "aria-label": "Module title",
            style: { fontSize: "1.2rem", fontWeight: 600 }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInput-underline:before": { borderBottomColor: "transparent" }
          }}
        />
        <NetworkSelector
          selectedNetworkId={targetNetworkId}
          onSelect={(net) =>
            onSelectNetwork(net ? { id: net.id, sponsorId: net.sponsorId } : null)
          }
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {catalogLoading ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                Loading catalog
              </Typography>
            </Stack>
          ) : null}
          {catalogError ? (
            <Tooltip title={catalogError}>
              <Chip
                label="Catalog error"
                color="error"
                variant="outlined"
                size="small"
              />
            </Tooltip>
          ) : null}
          <Chip
            label={savedRecently ? "Autosaved" : "Saving…"}
            color={savedRecently ? "success" : "default"}
            variant="outlined"
            size="small"
          />
          <Button
            size="small"
            startIcon={<DeleteSweepIcon />}
            variant="text"
            color="inherit"
            onClick={onReset}
          >
            Reset
          </Button>
          <Tooltip
            title={
              canExport ? "Export module CSV" : "Resolve blockers in the preview before exporting."
            }
          >
            <span>
              <Button
                startIcon={<DownloadIcon />}
                size="small"
                variant="contained"
                onClick={onExport}
                disabled={!canExport}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

function CollapsedRail({
  label,
  onExpand,
  side
}: {
  label: string;
  onExpand: () => void;
  side: "left" | "right";
}) {
  const Icon = label === "Preview" ? VisibilityIcon : MenuOpenIcon;
  return (
    <Paper
      variant="outlined"
      sx={{
        position: { lg: "sticky" },
        top: { lg: 16 },
        borderRadius: 2,
        height: 96,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Tooltip title={`Show ${label.toLowerCase()}`} placement={side === "left" ? "right" : "left"}>
        <IconButton size="small" onClick={onExpand}>
          <Icon
            fontSize="small"
            sx={
              label === "Outline"
                ? { transform: side === "left" ? "rotate(180deg)" : "none" }
                : undefined
            }
          />
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ writingMode: "vertical-rl", mt: 1, fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Paper>
  );
}

type SourceActivity = { id: string; title: string; description: string | null };

type WorkspaceProps = {
  instrument: InstrumentDraft;
  targetSponsorId?: string;
  sourceInstrumentDetail: GetInstrumentDetail | null;
  /** Activities under the source assessment, or null when not yet loaded /
   *  no source assessment selected. */
  sourceActivities: SourceActivity[] | null;
  /** True when the fetch for the source assessment's activities has failed.
   *  Lets the picker render an error + retry hint instead of spinning. */
  sourceActivitiesFailed: boolean;
  flatLibrary: FlatLibraryQuestion[];
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  catalogQuestionsById: Map<string, CatalogQuestion>;
  selectedIds: string[];
  requiredById: Record<string, boolean>;
  answerSetById: Record<string, string>;
  overridesById: Record<string, QuestionOverride>;
  onPatchInstrument: (patch: Partial<InstrumentDraft>) => void;
  onChangeInstrumentTitleAndSource: (next: {
    title: string;
    match: InstrumentSuggestion | null;
  }) => void;
  onChangeAssessmentTitleAndSource: (next: {
    title: string;
    match: AssessmentSuggestion | null;
  }) => void;
  onQuestionsChange: (next: {
    selectedIds: string[];
    requiredById: Record<string, boolean>;
    answerSetById: Record<string, string>;
    overridesById: Record<string, QuestionOverride>;
  }) => void;
};

function Workspace({
  instrument,
  targetSponsorId,
  sourceInstrumentDetail,
  sourceActivities,
  sourceActivitiesFailed,
  flatLibrary,
  flatByQuestionId,
  catalogQuestionsById,
  selectedIds,
  requiredById,
  answerSetById,
  overridesById,
  onPatchInstrument,
  onChangeInstrumentTitleAndSource,
  onChangeAssessmentTitleAndSource,
  onQuestionsChange
}: WorkspaceProps) {
  const settings = instrument.assessmentSettings;

  // Lowercased trimmed titles of activities under the source assessment.
  // Used to highlight which rows in the draft will reuse an existing DB
  // row at import time. Title-only matching mirrors the picker's
  // already-added indicator and the dedup chain in import.js.
  const reusedTitlesLower = useMemo(() => {
    if (!sourceActivities) return new Set<string>();
    return new Set(sourceActivities.map((a) => a.title.trim().toLowerCase()));
  }, [sourceActivities]);

  // The legend is only useful once a green tint actually appears on the
  // page — show it when the active instrument has any reuse signal.
  const hasAnyReuse = useMemo(() => {
    if (instrument.sourceInstrumentId) return true;
    if (instrument.sourceAssessmentId) return true;
    if (reusedTitlesLower.size === 0) return false;
    const rows = parseActivities(instrument);
    return rows.some((r) => {
      const t = r.title.trim().toLowerCase();
      return t && reusedTitlesLower.has(t);
    });
  }, [
    instrument,
    reusedTitlesLower
  ]);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      {hasAnyReuse ? <ReuseLegend /> : null}

      <SectionCard
        title="Instrument"
        reuseHighlight={Boolean(instrument.sourceInstrumentId)}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <InstrumentTitleAutocomplete
            value={instrument.title}
            sourceInstrumentId={instrument.sourceInstrumentId}
            required
            disabled={!targetSponsorId}
            onChange={(next) => onChangeInstrumentTitleAndSource(next)}
            sx={{ flex: "0 0 36%", minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Instrument description (optional)"
            fullWidth
            multiline
            maxRows={3}
            value={instrument.description}
            onChange={(e) => onPatchInstrument({ description: e.target.value })}
            disabled={Boolean(instrument.sourceInstrumentId)}
            sx={compactFieldSx}
          />
        </Stack>
      </SectionCard>

      <SectionCard
        title="Assessment"
        hint="Settings apply to every activity in this instrument when the module is imported."
        reuseHighlight={Boolean(instrument.sourceAssessmentId)}
      >
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <AssessmentTitleAutocomplete
              sourceInstrumentId={instrument.sourceInstrumentId}
              sponsorId={targetSponsorId}
              value={settings.title}
              required
              onChange={(n) => onChangeAssessmentTitleAndSource(n)}
              sx={{ flex: "0 0 36%", minWidth: 200 }}
            />
            <TextField
              size="small"
              label="Assessment description (optional)"
              fullWidth
              multiline
              maxRows={3}
              value={settings.description}
              onChange={(e) =>
                onPatchInstrument({
                  assessmentSettings: { ...settings, description: e.target.value }
                })
              }
              disabled={Boolean(instrument.sourceAssessmentId)}
              sx={compactFieldSx}
            />
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                size="small"
                checked={settings.requireSelfAssessment}
                disabled={Boolean(instrument.sourceAssessmentId)}
                onChange={(_, checked) =>
                  onPatchInstrument({
                    assessmentSettings: { ...settings, requireSelfAssessment: checked }
                  })
                }
              />
              <Typography variant="body2">Require trainee self-assessment</Typography>
            </Stack>
            <TextField
              size="small"
              label="Creation window (hours)"
              type="number"
              value={settings.creationDurationHours}
              disabled={Boolean(instrument.sourceAssessmentId)}
              onChange={(e) =>
                onPatchInstrument({
                  assessmentSettings: {
                    ...settings,
                    creationDurationHours: parseHours(
                      e.target.value,
                      DEFAULT_ASSESSMENT_SETTINGS.creationDurationHours
                    )
                  }
                })
              }
              sx={{ ...compactFieldSx, width: 200 }}
            />
            <TextField
              size="small"
              label="Expiration window (hours)"
              type="number"
              value={settings.expirationDurationHours}
              disabled={Boolean(instrument.sourceAssessmentId)}
              onChange={(e) =>
                onPatchInstrument({
                  assessmentSettings: {
                    ...settings,
                    expirationDurationHours: parseHours(
                      e.target.value,
                      DEFAULT_ASSESSMENT_SETTINGS.expirationDurationHours
                    )
                  }
                })
              }
              sx={{ ...compactFieldSx, width: 200 }}
            />
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title="Activities" hint="Procedures, scenarios, or rotations to assess.">
        <Stack spacing={1.5}>
          <ReusableActivitiesPicker
            sourceAssessmentId={instrument.sourceAssessmentId}
            sourceActivities={sourceActivities}
            sourceActivitiesFailed={sourceActivitiesFailed}
            currentRows={parseActivities(instrument)}
            onAppend={(rows) => {
              if (rows.length === 0) return;
              const existing = parseActivities(instrument);
              const next = [...existing, ...rows];
              onPatchInstrument({
                activitiesPasteText: next
                  .map((r) =>
                    [r.title, r.description, r.category]
                      .map((v) =>
                        (v ?? "").includes("\t") || (v ?? "").includes("\n") || (v ?? "").includes('"')
                          ? `"${(v ?? "").replace(/"/g, '""')}"`
                          : v ?? ""
                      )
                      .join("\t")
                  )
                  .join("\n")
              });
            }}
          />
          <ActivitiesEditor
            key={instrument.id}
            instrument={instrument}
            reusedTitlesLower={reusedTitlesLower}
            onChange={(next) => onPatchInstrument(next)}
          />
        </Stack>
      </SectionCard>

      <SectionCard
        title="Questions"
        hint={
          instrument.sourceInstrumentId
            ? "Inherited from the existing instrument and locked. Type a different instrument title to create a new one."
            : "Build the assessment form from library questions or custom ones."
        }
      >
        {instrument.sourceInstrumentId ? (
          <InheritedQuestionsList detail={sourceInstrumentDetail} />
        ) : (
          <QuestionsEditor
            key={instrument.id}
            instrument={instrument}
            flatLibrary={flatLibrary}
            flatByQuestionId={flatByQuestionId}
            catalogQuestionsById={catalogQuestionsById}
            selectedIds={selectedIds}
            requiredById={requiredById}
            answerSetById={answerSetById}
            overridesById={overridesById}
            onChange={onQuestionsChange}
          />
        )}
      </SectionCard>
    </Stack>
  );
}

function SectionCard({
  title,
  hint,
  reuseHighlight,
  children
}: {
  title: string;
  hint?: string;
  /** When true, paint a green left border + show a "Reuse" chip in the header
   *  to indicate this entity will be linked to an existing DB row at import. */
  reuseHighlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        ...(reuseHighlight
          ? {
              borderLeft: "3px solid",
              borderLeftColor: "success.main",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(76, 175, 80, 0.06)"
                  : "rgba(76, 175, 80, 0.04)"
            }
          : null)
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {reuseHighlight ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label="Reuse"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          ) : null}
        </Stack>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </Stack>
      {children}
    </Paper>
  );
}

function ReuseLegend() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        bgcolor: "action.hover"
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: 0.5,
          border: "2px solid",
          borderColor: "success.main",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(76, 175, 80, 0.15)"
              : "rgba(76, 175, 80, 0.1)"
        }}
      />
      <Typography variant="caption" color="text.secondary">
        Green = will reuse an existing record (instrument, assessment, or activity)
        instead of creating a new one on import.
      </Typography>
    </Stack>
  );
}

function parseHours(input: string, fallback: number): number {
  const n = parseInt(input, 10);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

function InheritedQuestionsList({ detail }: { detail: GetInstrumentDetail | null }) {
  if (!detail) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Loading inherited questions…
        </Typography>
      </Stack>
    );
  }
  const qs = detail.questions ?? [];
  if (qs.length === 0) {
    return (
      <Alert severity="warning" variant="outlined">
        This existing instrument has no questions in the database.
      </Alert>
    );
  }
  return (
    <Stack spacing={1}>
      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        {qs.length} question{qs.length === 1 ? "" : "s"} inherited from the existing
        instrument. Read-only — to use a different question set, change the instrument
        title to create a new instrument.
      </Alert>
      <Stack spacing={0.75}>
        {qs.map((q, idx) => {
          const inputName = q.inputType ?? "";
          const kindLabel = inputName.toLowerCase().includes("multi")
            ? "Multi"
            : inputName.toLowerCase().includes("text") ||
                inputName.toLowerCase().includes("feedback")
              ? "Feedback"
              : "Radio";
          return (
            <Paper
              key={q.id}
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "background.default" }}
            >
              <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {idx + 1}.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {q.title}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={kindLabel}
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
                {q.required ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Required"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                  />
                ) : null}
              </Stack>
              {q.defaultPrompt ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  {q.defaultPrompt}
                </Typography>
              ) : null}
              {q.answerOptions && q.answerOptions.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {q.answerOptions.slice(0, 6).map((a) => (
                    <Chip
                      key={a.id}
                      size="small"
                      variant="outlined"
                      label={a.anchor || a.value}
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  ))}
                  {q.answerOptions.length > 6 ? (
                    <Typography variant="caption" color="text.secondary">
                      +{q.answerOptions.length - 6} more
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
